'use es6';

import { memoize, omit } from 'underscore';
import I18n from 'I18n';
import DisplayTypes from 'ContentUtils/constants/CustomWidgetFieldDisplayTypes';
import * as FieldTypes from 'ContentUtils/constants/CustomWidgetFieldTypes';
import UrlTypes from 'ContentUtils/enums/UrlTypes';
import { SLUG } from 'ContentUtils/enums/TagDefaultValueTypes';
import Routes from 'ContentUtils/Routes';
import { EmbedTypes, oEmbedTypes } from 'ContentUtils/constants/MediaTypes';
import { DEFAULT, DISPLAY, CHOICES, RESIZABLE, RESPONSIVE, SHOW_LOADING, VALIDATION_REGEX, STEP, PICKER, ICON_SET, TAG_VALUE, SUPPORTED_TYPES, SUPPORTED_OEMBED_TYPES, SUPPORTED_SOURCE_TYPES, SHOW_ADVANCED_REL_OPTIONS } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { DrawerTypes } from 'FileManagerLib/Constants';
import { EMAIL, ANY } from 'DesignData/enums/CustomWidgetHostTemplateTypes';
import { generateGuid } from 'DesignData/lib/id';
import ImmutableModel from 'DesignData/models/ImmutableModel';
import { getResponsiveImageSizeType } from 'DesignData/lib/layoutTreeDataHelper';
import { ICON_SETS } from 'ContentUtils/constants/IconSets';
import * as FieldHublSnippetHelpers from 'DesignData/helpers/FieldHublSnippetHelpers';
import { USE_CONTENT_TYPES } from 'DesignData/enums/Gates';
const DEFAULT_FIELD_NAME = 'field_name';

const fieldI18nKey = key => `designdata.models.customWidgetField.${key}`;

const getUrlTypes = urlSupportedTypePermissions => {
  return Object.values(UrlTypes || {}).filter(type => {
    return urlSupportedTypePermissions[type] !== false;
  });
};

export default class CustomWidgetField extends ImmutableModel {
  /**
   * @param {Object} props - The properties of the field.
   * @param {String} props.name - Name of field to be used in widget source.
   * @param {String} props.type - Type of field (see ContentUtils/constants/CustomWidgetFieldTypes).
   * @param {String} props.label - Label of field to be displayed in editors.
   * @param {Object} props.default - Default value for field type.
   * @param {String} props.help_text - Help text to be displayed in editors.
   * @param {Object} props.occurrence - Occurence object
   */
  constructor(_props) {
    let props = _props;

    if (!props.id) {
      props = Object.assign({}, props, {
        id: generateGuid()
      });
    }

    if (props.children) {
      const children = props.children.map(_child => new CustomWidgetField(_child));
      props = Object.assign({}, props, {
        children
      });
    }

    if (!props.isNew) {
      return super(props);
    }

    const defaults = CustomWidgetField.getDefaults(props.type, props.fieldCreateOptions);
    return super(Object.assign({}, omit(props, 'isNew', 'fieldCreateOptions'), {}, defaults));
  }

  getDefaults(type = this.type) {
    return CustomWidgetField.getDefaults(type);
  }

  getFieldTypeLabel(type = this.type) {
    return CustomWidgetField.getFieldTypeLabel(type);
  }

  getDefaultLabel(type = this.type) {
    return CustomWidgetField.getDefaultLabel(type);
  }

  getSnippet(fieldName) {
    const name = fieldName || this.name || DEFAULT_FIELD_NAME;

    switch (this.type) {
      case FieldTypes.IMAGE:
        {
          return {
            snippet: `{% if widget.${name}.src %}\n\t<img src="{{ widget.${name}.src }}" width="{{ widget.${name}.width }}" height="{{ widget.${name}.height }}" alt="{{ widget.${name}.alt }}">\n{% endif %}`,
            truncatedSnippet: `{% if widget.${name}.src %}<img src="{{ widget.${name}.src }}">{% endif %}`
          };
        }

      case FieldTypes.BOOLEAN:
        {
          return {
            snippet: `{% if widget.${name} %}<!-- HTML to show when checked -->{% endif %}`,
            truncatedSnippet: `{% if widget.${name} %}...{% endif %}`
          };
        }

      default:
        {
          return {
            snippet: `{{ widget.${name} }}`
          };
        }
    }
  }

  getModuleSnippet(fieldName, options = {}) {
    const name = fieldName || this.name || DEFAULT_FIELD_NAME;
    const {
      occurrenceOfId,
      level,
      hostTemplateTypes = [],
      contentTypes = [],
      hasInstalledMediaBridgeProviders
    } = options;
    let parentName = 'module';

    if (occurrenceOfId) {
      parentName = level > 0 ? `item${level + 1}` : 'item';
    }

    if (this.occurrence && occurrenceOfId !== this.id) {
      const _level = level || level === 0 ? level + 1 : 0;

      const _itemName = _level > 0 ? `item${_level + 1}` : 'item';

      const _occurrenceContent = this.getModuleSnippet(name, {
        occurrenceOfId: this.id,
        level: _level,
        hostTemplateTypes,
        contentTypes
      }).split('\n').join('\n\t');

      return `{% for ${_itemName} in ${parentName}.${name} %}\n\t${_occurrenceContent}\n{% endfor %}`;
    }

    const fieldNamePath = occurrenceOfId === this.id ? parentName : `${parentName}.${name}`;
    const isEmailOrAnyType = Routes.isUngated(USE_CONTENT_TYPES) ? contentTypes.includes(EMAIL) || contentTypes.includes(ANY) : hostTemplateTypes.includes(EMAIL) || hostTemplateTypes.includes(ANY);

    switch (this.type) {
      case FieldTypes.GROUP:
        {
          if (!this.children || !this.children.length) {
            return `{{ ${fieldNamePath} }}`;
          }

          return this.children.map(_child => {
            const _childFieldName = occurrenceOfId === this.id ? _child.name : `${name}.${_child.name}`;

            return _child.getModuleSnippet(_childFieldName, {
              occurrenceOfId,
              level,
              hostTemplateTypes,
              contentTypes
            });
          }).join('\n');
        }

      case FieldTypes.IMAGE:
        return `{% if ${fieldNamePath}.src %}\n\t{% set sizeAttrs = 'width="{{ ${fieldNamePath}.width }}" height="{{ ${fieldNamePath}.height }}"' %}\n\t{% if ${fieldNamePath}.size_type == 'auto' %}\n\t\t{% set sizeAttrs = 'width="{{ ${fieldNamePath}.width }}" height="{{ ${fieldNamePath}.height }}" style="max-width: 100%; height: auto;"' %}\n\t{% elif ${fieldNamePath}.size_type == 'auto_custom_max' %}\n\t\t{% set sizeAttrs = 'width="{{ ${fieldNamePath}.max_width }}" height="{{ ${fieldNamePath}.max_height }}" style="max-width: 100%; height: auto;"' %}\n\t{% endif %}\n\t {% set loadingAttr = ${fieldNamePath}.loading != 'disabled' ? 'loading="{{ ${fieldNamePath}.loading }}"' : '' %}\n\t<img src="{{ ${fieldNamePath}.src }}" alt="{{ ${fieldNamePath}.alt }}" {{ loadingAttr }} {{ sizeAttrs }}>\n{% endif %}`;

      case FieldTypes.BOOLEAN:
        return `{% if ${fieldNamePath} %}\n\t<!-- HTML to show when checked -->\n{% endif %}`;

      case FieldTypes.CTA:
        return `{% cta guid="{{ ${fieldNamePath} }}" %}`;

      case FieldTypes.SIMPLE_MENU:
        return `{% simple_menu menu_tree="{{ ${fieldNamePath} }}" %}`;

      case FieldTypes.MENU:
        return `{% menu id="{{ ${fieldNamePath} }}" %}`;

      case FieldTypes.FORM:
        if (!this.default || typeof this.default === 'string') {
          return `{% form\n\tform_to_use="{{ ${fieldNamePath} }}"\n%}`;
        }

        return `{% form\n\tform_to_use="{{ ${fieldNamePath}.form_id }}"\n\tresponse_response_type="{{ ${fieldNamePath}.response_type }}"\n\tresponse_message="{{ ${fieldNamePath}.message }}"\n\tresponse_redirect_id="{{ ${fieldNamePath}.redirect_id }}"\n\tresponse_redirect_url="{{${fieldNamePath}.redirect_url}}"\n\tgotowebinar_webinar_key="{{ ${fieldNamePath}.gotowebinar_webinar_key }}"\n%}`;

      case FieldTypes.RICH_TEXT:
        return `{% inline_rich_text field="${name}" value="{{ ${fieldNamePath} }}" %}`;

      case FieldTypes.TEXT:
        return `{% inline_text field="${name}" value="{{ ${fieldNamePath} }}" %}`;

      case FieldTypes.PAGE:
        return `{{ content_by_id(${fieldNamePath}) }}`;

      case FieldTypes.LOGO:
        return `{% set loadingAttr = ${fieldNamePath}.loading != 'disabled' ? '{{ ${fieldNamePath}.loading }}' : '' %}\n\n{% logo\n\toverride_inherited_src="{{ ${fieldNamePath}.override_inherited_src }}"\n\tsrc="{{ ${fieldNamePath}.src }}"\n\talt="{{ ${fieldNamePath}.alt }}"\n\twidth="{{ ${fieldNamePath}.width }}"\n\theight="{{ ${fieldNamePath}.height }}"\n\tloading="{{ loadingAttr }}"\n\tsuppress_company_name="{{ ${fieldNamePath}.suppress_company_name }}"\n%}`;

      case FieldTypes.COLOR:
        {
          const opacity = !isEmailOrAnyType ? `\n{{ ${fieldNamePath}.opacity }}` : '';
          return !this.default || typeof this.default === 'string' ? `{{ ${fieldNamePath} }}` : `{{ ${fieldNamePath}.color }}${opacity}`;
        }

      case FieldTypes.ICON:
        return `{% icon\n\tname="{{ ${fieldNamePath}.name }}"\n\tstyle="{{ ${fieldNamePath}.type }}"\n\tunicode="{{ ${fieldNamePath}.unicode }}"\n\ticon_set="{{ ${fieldNamePath}.icon_set }}"\n%}`;

      case FieldTypes.FONT:
        {
          return `<span style="font-family: {{ ${fieldNamePath}.font }}; {{ ${fieldNamePath}.style }}; color: {{ ${fieldNamePath}.color }}; font-size: {{ ${fieldNamePath}.size }}{{${fieldNamePath}.size_unit }};">Hello World</span>`;
        }

      case FieldTypes.VIDEO:
        {
          return FieldHublSnippetHelpers.getVideoFieldSnippet(fieldNamePath);
        }

      case FieldTypes.URL:
        {
          return `{{ ${fieldNamePath}.href }}`;
        }

      case FieldTypes.LINK:
        {
          return `{% set href = ${fieldNamePath}.url.href %}\n{% if ${fieldNamePath}.url.type is equalto "EMAIL_ADDRESS" %}\n\t{% set href = "mailto:" + href %}\n{% endif %}\n<a href="{{ href }}"\n\t{% if ${fieldNamePath}.open_in_new_tab %}target="_blank"{% endif %}\n\t{% if ${fieldNamePath}.rel %}rel="{{ ${fieldNamePath}.rel }}"{% endif %}\n\t>\n\tLink text\n</a>`;
        }

      case FieldTypes.CRM_OBJECT:
        {
          return `{{ ${fieldNamePath}.object_type }}\n{{ ${fieldNamePath}.id }}\n{{ ${fieldNamePath}.properties }}`;
        }

      case FieldTypes.CRM_OBJECT_PROPERTY:
        {
          return `{{ ${fieldNamePath}.property }}\n{{ ${fieldNamePath}.property_definition.label }}\n{{ ${fieldNamePath}.property_definition.type }}`;
        }

      case FieldTypes.EMBED:
        {
          if (Routes.isUngated(USE_CONTENT_TYPES) ? contentTypes.length === 1 && contentTypes.includes(EMAIL) : hostTemplateTypes.length === 1 && hostTemplateTypes.includes(EMAIL)) {
            // Email-only
            return `{% set oembedData = ${fieldNamePath}.source_type == 'media_bridge' ? ${fieldNamePath}.media_bridge_object : ${fieldNamePath} %}\n{{ oembedData.oembed_response || oembed({ url: oembedData.oembed_url }) }}`;
          } else if (!isEmailOrAnyType) {
            // Doesn't support email
            return FieldHublSnippetHelpers.getEmbedFieldSnippetWithCssAndJs(fieldNamePath, hasInstalledMediaBridgeProviders);
          } // Supports both email AND non-email


          return `{{ ${fieldNamePath}.source_type == 'media_bridge' ? ${fieldNamePath}.media_bridge_object.oembed_response : ${fieldNamePath}.oembed_response }}`;
        }

      case FieldTypes.HUBDB_ROW:
        {
          return `{{ ${fieldNamePath}.table_id }}\n{{ ${fieldNamePath}.table_name }}\n{{ ${fieldNamePath}.id }}\n{{ ${fieldNamePath}.columns }}`;
        }

      case FieldTypes.PAYMENT:
        {
          return FieldHublSnippetHelpers.getPaymentFieldEmbedCode(fieldNamePath);
        }

      default:
        return `{{ ${fieldNamePath} }}`;
    }
  }

}
CustomWidgetField.getDefaults = memoize((type, fieldCreateOptions) => {
  const {
    isEmailOrAnyType,
    urlSupportedTypePermissions
  } = fieldCreateOptions || {};
  let defaults = {};

  switch (type) {
    case FieldTypes.BOOLEAN:
      {
        defaults = {
          [DEFAULT]: false,
          [DISPLAY]: DisplayTypes[FieldTypes.BOOLEAN].CHECKBOX
        };
        break;
      }

    case FieldTypes.COLOR:
      {
        defaults = {
          [DEFAULT]: {
            color: null,
            opacity: null
          }
        };
        break;
      }

    case FieldTypes.CHOICE:
      {
        const defaultChoiceValue = I18n.text(fieldI18nKey('defaults.choice.value'));
        const defaultChoiceLabel = I18n.text(fieldI18nKey('defaults.choice.label'));
        defaults = {
          [DISPLAY]: DisplayTypes[FieldTypes.CHOICE].SELECT,
          [CHOICES]: [[`${defaultChoiceValue} 1`, `${defaultChoiceLabel} 1`], [`${defaultChoiceValue} 2`, `${defaultChoiceLabel} 2`]]
        };
        break;
      }

    case FieldTypes.IMAGE:
      {
        const imageDefault = {
          src: '',
          alt: null,
          loading: 'lazy'
        };
        defaults = {
          [DEFAULT]: isEmailOrAnyType ? imageDefault : getResponsiveImageSizeType(imageDefault),
          [RESIZABLE]: true,
          [RESPONSIVE]: true,
          [SHOW_LOADING]: false
        };
        break;
      }

    case FieldTypes.TEXT:
      {
        defaults = {
          [VALIDATION_REGEX]: ''
        };
        break;
      }

    case FieldTypes.NUMBER:
      {
        defaults = {
          [DISPLAY]: DisplayTypes[FieldTypes.NUMBER].TEXT,
          [STEP]: 1
        };
        break;
      }

    case FieldTypes.DATE_TIME:
      {
        defaults = {
          [STEP]: 30
        };
        break;
      }

    case FieldTypes.FILE:
      {
        defaults = {
          [PICKER]: DrawerTypes.FILE.toLowerCase()
        };
        break;
      }

    case FieldTypes.LOGO:
      {
        defaults = {
          [DEFAULT]: {
            override_inherited_src: false,
            src: null,
            alt: null
          }
        };
        break;
      }

    case FieldTypes.FORM:
      {
        defaults = {
          [DEFAULT]: {
            response_type: 'inline',
            message: I18n.text(fieldI18nKey('defaults.form.message'))
          }
        };
        break;
      }

    case FieldTypes.SIMPLE_MENU:
      {
        defaults = {
          [DEFAULT]: []
        };
        break;
      }

    case FieldTypes.SURVEY:
      {
        defaults = {
          [DEFAULT]: {
            survey_type: 'CES'
          }
        };
        break;
      }

    case FieldTypes.ICON:
      {
        defaults = {
          [DEFAULT]: {},
          [ICON_SET]: ICON_SETS['fontawesome-5.14.0']
        };
        break;
      }

    case FieldTypes.FONT:
      {
        defaults = {
          [DEFAULT]: {}
        };
        break;
      }

    case FieldTypes.TAG:
      {
        defaults = {
          [TAG_VALUE]: SLUG
        };
        break;
      }

    case FieldTypes.URL:
      {
        defaults = {
          [DEFAULT]: {
            content_id: null,
            href: '',
            type: UrlTypes.EXTERNAL
          },
          [SUPPORTED_TYPES]: getUrlTypes(urlSupportedTypePermissions)
        };
        break;
      }

    case FieldTypes.LINK:
      {
        defaults = {
          [DEFAULT]: {
            url: {
              content_id: null,
              type: UrlTypes.EXTERNAL,
              href: ''
            },
            open_in_new_tab: false,
            no_follow: false
          },
          [SUPPORTED_TYPES]: getUrlTypes(urlSupportedTypePermissions)
        };
        break;
      }

    case FieldTypes.EMBED:
      {
        defaults = {
          [DEFAULT]: {
            source_type: EmbedTypes.OEMBED
          },
          [SUPPORTED_SOURCE_TYPES]: isEmailOrAnyType ? [EmbedTypes.OEMBED] : [EmbedTypes.OEMBED, EmbedTypes.HTML],
          [SUPPORTED_OEMBED_TYPES]: Object.values(oEmbedTypes),
          [SHOW_ADVANCED_REL_OPTIONS]: false
        };
        break;
      }

    default:
      break;
  }

  return Object.assign({}, defaults, {
    label: CustomWidgetField.getFieldTypeLabel(type)
  });
}, (type, fieldCreateOptions) => {
  return `${type}-${JSON.stringify(fieldCreateOptions)}`;
});
CustomWidgetField.getDefaultLabel = memoize(type => {
  const fieldTypeLabel = CustomWidgetField.getFieldTypeLabel(type);

  if (type === FieldTypes.GROUP) {
    return fieldTypeLabel;
  }

  return I18n.text(fieldI18nKey('defaults.label.field'), {
    fieldType: fieldTypeLabel
  });
});
CustomWidgetField.getFieldTypeLabel = memoize(type => {
  return I18n.text(fieldI18nKey(`defaults.label.fieldTypes.${type}`));
});